import { Button, Group, Modal, Stack } from '@mantine/core';
import { primaryColor } from '../../../../constants/theme';
import { IconCheck } from '@tabler/icons-react';
import { BookingResponseModel } from '../../../../types/BookingModels';
import { useMutation } from '@tanstack/react-query';
import { useDisclosure } from '@mantine/hooks';
import { ErrorModal } from '../error/ErrorModal';
import { confirmBooking } from '../../../../services/BookingService';
import { BookingPreview } from './BookingPreview';

interface AcceptButtonProps {
  groupedBookings: BookingResponseModel[];
  profileId: string;
  refreshBookings: () => void;
  token: string | undefined;
  reloadOnSuccess?: boolean;
}

export const AcceptButton = ({
  groupedBookings,
  profileId,
  refreshBookings,
  token,
  reloadOnSuccess,
}: AcceptButtonProps) => {
  const booking = groupedBookings[0];
  const isAccepted = booking.accepted;
  const isCreator = booking.professionalId === profileId;
  const [opened, { open, close }] = useDisclosure(false);
  const endInPast = new Date(booking.endIso) < new Date();

  if (!isCreator || isAccepted || endInPast || booking.isCancelled) return null;

  const { isError, isPending, mutate, reset } = useMutation({
    mutationKey: ['accept-booking', booking.id],
    mutationFn: async () => {
      const confirmBookingPayload = {
        bookingIds: groupedBookings.map((b) => b.id),
        userId: booking.userId,
      };
      await confirmBooking(confirmBookingPayload, token || '');
      return Promise.resolve();
    },
    onSuccess: () => {
      refreshBookings();
      close();
      if (reloadOnSuccess) {
        window.location.reload();
      }
    },
    onError: (error) => {
      console.error('Error accepting booking:', error);
      close();
    },
  });

  const onRetry = () => {
    mutate();
    open();
  };

  return (
    <>
      <ErrorModal
        isInError={isError}
        errorMessage="There was an error accepting the booking. Please try again."
        dismissAction={() => reset()}
        retryAction={onRetry}
        isLoading={isPending}
      />
      <Modal
        opened={opened}
        onClose={close}
        closeOnEscape={false}
        withCloseButton={false}
        closeOnClickOutside={false}
      >
        <Stack justify="center" align="center" gap={10}>
          <BookingPreview
            title="Confirm booking?"
            groupedBookings={groupedBookings}
            bottomText="Please note that any other booking requests for this time slot will be declined automatically."
            titleTestId="confirm-booking-modal-title"
            textTestId="confirm-booking-modal-text"
          />
          <Group justify="center" gap={10} w="100%">
            <Button
              color={primaryColor}
              w="48%"
              variant="outline"
              onClick={close}
              data-testid="confirm-modal-cancel-cta"
            >
              Cancel
            </Button>
            <Button
              color={primaryColor}
              w="48%"
              loading={isPending}
              onClick={() => mutate()}
              data-testid="confirm-modal-confirm-cta"
            >
              Accept
            </Button>
          </Group>
        </Stack>
      </Modal>
      <Button
        radius="md"
        variant="outline"
        color={primaryColor}
        leftSection={<IconCheck size={16} />}
        w={105}
        onClick={open}
        data-testid={`confirm-button-${booking.id}`}
      >
        Accept
      </Button>
    </>
  );
};
